/* interiorLayout.js — Phase-1 INTERIOR ACCESSORIES (the equipment library the
 * Cabinet Technician already authors: internal shelves, drawer organizers,
 * bins, pull-outs, corners, columns — with prices and per-product
 * allow-lists) finally consumed by the customer configurator.
 *
 * Config schema (per instance, additive LAYERS — several accessories coexist):
 *   cfg.interior = [{ cat: "shelves"|"organizers"|"bins"|"corners"|
 *                          "pullouts"|"columns", code: "<equipment code>",
 *                     qty: 1..10 }]
 *
 * This module is the ONE canonical layout: simple physical stand-ins placed
 * inside the carcass box in cabinet-local metres (origin = carcass centre,
 * +z = front), consumed by BOTH the orbit scene and Present so the two
 * surfaces stack identical geometry. Accessory-specific GLBs arrive with the
 * component-library phase; these canonical forms are deliberately simple
 * (shelf board / tray / basket block), never fake product detail. */

import { entityModel } from "../../catalog/lib/entities";

export const INTERIOR_CATS = ["shelves", "organizers", "bins", "corners", "pullouts", "columns"];

/** normalizeInterior(cfg) → clean [{cat, code, qty}] (defensive: blob-adjacent). */
export function normalizeInterior(cfg) {
  const list = cfg && Array.isArray(cfg.interior) ? cfg.interior : [];
  const out = [];
  list.forEach((e) => {
    if (!e || typeof e !== "object") return;
    const cat = INTERIOR_CATS.indexOf(e.cat) >= 0 ? e.cat : null;
    const code = typeof e.code === "string" && e.code ? e.code : null;
    if (!cat || !code) return;
    const qty = Math.max(1, Math.min(10, parseInt(e.qty, 10) || 1));
    out.push({ cat, code, qty });
  });
  return out;
}

/** interiorVisuals(def, cfg, blob?) → [{kind, code, cat, model, x,y,z, w,h,d}]
 *  in cabinet-local metres. Passing the catalog blob lets each accessory carry
 *  its own registry GLB (Cabinet Technician V2); without it the canonical
 *  stand-ins are returned exactly as before. */
export function interiorVisuals(def, cfg, blob) {
  const list = normalizeInterior(cfg);
  if (!list.length || !def) return [];
  const W = def.width || 0.6, H = def.height || 0.78, D = def.depth || 0.57;
  const iw = Math.max(0.1, W - 0.05);            // clear of the side panels
  const idp = Math.max(0.1, D - 0.09);           // clear of the front + back
  const rows = [];
  list.forEach((e) => { for (let i = 0; i < e.qty; i++) rows.push(e); });
  const n = rows.length;
  const out = [];
  rows.forEach((e, i) => {
    const t = (i + 1) / (n + 1);                 // even bottom→top distribution
    const y = -H / 2 + 0.06 + t * (H - 0.16);
    // COMPONENT REGISTRY (V2): when the accessory's entity owns its own GLB,
    // the slot carries the model url and the renderers fit that geometry into
    // exactly this box. Without one they draw the canonical stand-in — never a
    // faked product shape. `blob` is optional so every existing caller works.
    const model = blob ? entityModel(blob, "equipment." + e.cat, e.code) : null;
    if (e.cat === "shelves") {
      out.push({ kind: "shelf", code: e.code, cat: e.cat, model, x: 0, y, z: -0.015, w: iw, h: 0.018, d: idp });
    } else if (e.cat === "bins" || e.cat === "pullouts" || e.cat === "columns") {
      out.push({ kind: "bin", code: e.code, cat: e.cat, model, x: 0, y: Math.max(y, -H / 2 + 0.13), z: -0.02, w: iw * 0.86, h: Math.min(0.24, H * 0.28), d: idp * 0.9 });
    } else {                                     // organizers / corners → slim tray
      out.push({ kind: "tray", code: e.code, cat: e.cat, model, x: 0, y, z: -0.015, w: iw * 0.9, h: 0.05, d: idp * 0.9 });
    }
  });
  return out;
}
